import React, { useState, useEffect, useRef } from 'react';
import SpaceBackground from '../components/layout/SpaceBackground';
import {
  Mic, MicOff, Send, Loader2, Trash2, CheckCircle, XCircle,
  Clock, FileText, Calendar, Briefcase, User, Mail, Play, Square,
  ChevronDown, ChevronRight, Edit2, Save, X
} from 'lucide-react';
import api from '../services/api';

const Dictate = () => {
  const [dictations, setDictations] = useState([]);
  const [currentDictation, setCurrentDictation] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cases, setCases] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [expandedActions, setExpandedActions] = useState({});
  const [editingAction, setEditingAction] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    loadDictations();
    loadCasesAndContacts();
  }, []);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRecording]);

  const loadDictations = async () => {
    try {
      const response = await api.get('/dictate/');
      setDictations(response.data);
    } catch (error) {
      console.error('Error loading dictations:', error);
    }
  };

  const loadCasesAndContacts = async () => {
    try {
      const [casesRes, contactsRes] = await Promise.all([
        api.get('/virgo/cases?limit=100'),
        api.get('/contacts/?limit=100')
      ]);
      setCases(casesRes.data.cases || casesRes.data || []);
      setContacts(contactsRes.data.contacts || contactsRes.data || []);
    } catch (error) {
      console.error('Error loading cases/contacts:', error);
    }
  };

  const loadDictation = async (id) => {
    setLoading(true);
    try {
      const response = await api.get(`/dictate/${id}`);
      setCurrentDictation(response.data);
      // Expand all actions by default
      const expanded = {};
      response.data.actions?.forEach((_, idx) => {
        expanded[idx] = true;
      });
      setExpandedActions(expanded);
    } catch (error) {
      console.error('Error loading dictation:', error);
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await uploadAudio(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const uploadAudio = async (audioBlob) => {
    setProcessing(true);
    try {
      // Create dictation record first
      const createResponse = await api.post('/dictate/');
      const dictationId = createResponse.data.id;

      // Upload audio file
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const uploadResponse = await api.post(`/dictate/${dictationId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await loadDictations();
      await loadDictation(dictationId);
    } catch (error) {
      console.error('Error uploading audio:', error);
      alert('Error processing dictation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const updateAction = async (actionId, updates) => {
    try {
      await api.patch(`/dictate/actions/${actionId}`, updates);
      if (currentDictation) {
        await loadDictation(currentDictation.id);
      }
    } catch (error) {
      console.error('Error updating action:', error);
    }
  };

  const approveAction = async (actionId) => {
    await updateAction(actionId, { is_approved: true });
  };

  const executeAction = async (actionId) => {
    try {
      await api.post(`/dictate/actions/${actionId}/execute`);
      if (currentDictation) {
        await loadDictation(currentDictation.id);
      }
      await loadDictations();
    } catch (error) {
      console.error('Error executing action:', error);
      alert('Error executing action: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteDictation = async (id) => {
    if (!window.confirm('Delete this dictation?')) return;
    try {
      await api.delete(`/dictate/${id}`);
      if (currentDictation?.id === id) {
        setCurrentDictation(null);
      }
      await loadDictations();
    } catch (error) {
      console.error('Error deleting dictation:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      recording: 'bg-red-100 text-red-800',
      transcribing: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      pending_review: 'bg-cyan-100 text-cyan-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-virgo-navy-800 text-virgo-teal-100'}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  const getActionIcon = (type) => {
    const icons = {
      note: FileText,
      calendar_event: Calendar,
      case_note: Briefcase,
      case_task: Briefcase,
      contact_note: User,
      email_draft: Mail
    };
    const Icon = icons[type] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  const ActionCard = ({ action, index }) => {
    const isExpanded = expandedActions[index];
    const [localData, setLocalData] = useState(action.action_data);

    const handleSave = async () => {
      await updateAction(action.id, { action_data: localData });
      setEditingAction(null);
    };

    return (
      <div className="border rounded-lg mb-3 overflow-hidden">
        <div 
          className="flex items-center justify-between p-3 bg-virgo-navy-600 cursor-pointer"
          onClick={() => setExpandedActions(prev => ({ ...prev, [index]: !prev[index] }))}
        >
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {getActionIcon(action.action_type)}
            <span className="font-medium capitalize">{action.action_type.replace('_', ' ')}</span>
            {action.is_executed && <CheckCircle className="w-4 h-4 text-green-500" />}
            {action.is_approved && !action.is_executed && <Clock className="w-4 h-4 text-yellow-500" />}
          </div>
          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            {!action.is_executed && (
              <>
                {!action.is_approved ? (
                  <button
                    onClick={() => approveAction(action.id)}
                    className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Approve
                  </button>
                ) : (
                  <button
                    onClick={() => executeAction(action.id)}
                    className="px-2 py-1 text-xs bg-virgo-teal-500 text-white rounded hover:bg-virgo-teal-600"
                  >
                    Execute
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-3 border-t">
            {editingAction === action.id ? (
              <div className="space-y-2">
                <textarea
                  value={JSON.stringify(localData, null, 2)}
                  onChange={(e) => {
                    try {
                      setLocalData(JSON.parse(e.target.value));
                    } catch {}
                  }}
                  className="w-full h-32 p-2 border rounded font-mono text-sm"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    className="px-3 py-1 bg-green-500 text-white rounded text-sm flex items-center gap-1"
                  >
                    <Save className="w-3 h-3" /> Save
                  </button>
                  <button
                    onClick={() => setEditingAction(null)}
                    className="px-3 py-1 bg-gray-300 rounded text-sm flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-start mb-2">
                  <div className="text-sm text-virgo-teal-300 space-y-1">
                    {Object.entries(action.action_data).map(([key, value]) => (
                      <div key={key}>
                        <span className="font-medium">{key}:</span> {String(value)}
                      </div>
                    ))}
                  </div>
                  {!action.is_executed && (
                    <button
                      onClick={() => setEditingAction(action.id)}
                      className="p-1 hover:bg-virgo-navy-800 rounded"
                    >
                      <Edit2 className="w-4 h-4 text-virgo-teal-400" />
                    </button>
                  )}
                </div>
                
                {/* Link to case/contact */}
                {!action.is_executed && (action.action_type.includes('case') || action.action_type.includes('contact')) && (
                  <div className="mt-3 pt-3 border-t space-y-2">
                    {action.action_type.includes('case') && (
                      <select
                        value={action.case_id || ''}
                        onChange={(e) => updateAction(action.id, { case_id: e.target.value || null })}
                        className="w-full p-2 border rounded text-sm"
                      >
                        <option value="">-- Link to Case --</option>
                        {cases.map(c => (
                          <option key={c.id} value={c.id}>{c.client_name} - {c.case_name}</option>
                        ))}
                      </select>
                    )}
                    {action.action_type.includes('contact') && (
                      <select
                        value={action.contact_id || ''}
                        onChange={(e) => updateAction(action.id, { contact_id: e.target.value || null })}
                        className="w-full p-2 border rounded text-sm"
                      >
                        <option value="">-- Link to Contact --</option>
                        {contacts.map(c => (
                          <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {action.is_executed && action.result_entity_type && (
                  <div className="mt-2 text-sm text-green-600">
                    ✓ Created {action.result_entity_type} #{action.result_entity_id}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full relative">
      <SpaceBackground />
      {/* Sidebar - Dictation List */}
      <div className="w-80 bg-virgo-navy-700/80 backdrop-blur-sm border-r flex flex-col relative z-10">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Mic className="w-5 h-5" />
            Dictate
          </h2>
        </div>
        
        {/* Recording Button */}
        <div className="p-4 border-b">
          {processing ? (
            <div className="flex items-center justify-center gap-2 p-4 bg-blue-50 rounded-lg">
              <Loader2 className="w-5 h-5 animate-spin text-virgo-teal-300" />
              <span className="text-blue-700">Processing...</span>
            </div>
          ) : isRecording ? (
            <div className="text-center">
              <div className="text-2xl font-mono text-red-600 mb-2">{formatTime(recordingTime)}</div>
              <button
                onClick={stopRecording}
                className="w-full py-3 bg-red-500 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-red-600"
              >
                <Square className="w-5 h-5" />
                Stop Recording
              </button>
            </div>
          ) : (
            <button
              onClick={startRecording}
              className="w-full py-3 bg-indigo-500 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-indigo-600"
            >
              <Mic className="w-5 h-5" />
              Start Recording
            </button>
          )}
        </div>

        {/* Dictation List */}
        <div className="flex-1 overflow-y-auto">
          {dictations.length === 0 ? (
            <div className="p-4 text-center text-virgo-teal-400">
              No dictations yet. Start recording!
            </div>
          ) : (
            dictations.map(d => (
              <div
                key={d.id}
                onClick={() => loadDictation(d.id)}
                className={`p-3 border-b cursor-pointer hover:bg-virgo-navy-600 ${
                  currentDictation?.id === d.id ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {d.detected_intent || 'Processing...'}
                    </div>
                    <div className="text-xs text-virgo-teal-400 mt-1">
                      {new Date(d.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(d.status)}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteDictation(d.id);
                      }}
                      className="p-1 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
                {d.action_count > 0 && (
                  <div className="text-xs text-virgo-teal-400 mt-1">
                    {d.action_count} action{d.action_count !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col relative z-10">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : currentDictation ? (
          <>
            {/* Header */}
            <div className="p-4 bg-virgo-navy-700 border-b">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-semibold">{currentDictation.detected_intent || 'Dictation'}</h3>
                  <div className="text-sm text-virgo-teal-400">
                    {new Date(currentDictation.created_at).toLocaleString()}
                  </div>
                </div>
                {getStatusBadge(currentDictation.status)}
              </div>
            </div>

            {/* Transcription */}
            <div className="p-4 bg-virgo-navy-700 border-b">
              <h4 className="font-medium text-sm text-virgo-teal-200 mb-2">Transcription</h4>
              <div className="p-3 bg-virgo-navy-600 rounded-lg text-sm">
                {currentDictation.raw_transcription || 'No transcription available'}
              </div>
              {currentDictation.transcription_confidence && (
                <div className="text-xs text-virgo-teal-400 mt-1">
                  Confidence: {currentDictation.transcription_confidence}%
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex-1 overflow-y-auto p-4">
              <h4 className="font-medium text-sm text-virgo-teal-200 mb-3">
                Detected Actions ({currentDictation.actions?.length || 0})
              </h4>
              
              {currentDictation.actions?.length > 0 ? (
                <>
                  {currentDictation.actions.map((action, idx) => (
                    <ActionCard key={action.id} action={action} index={idx} />
                  ))}
                  
                  {/* Bulk actions */}
                  {currentDictation.actions.some(a => !a.is_approved) && (
                    <button
                      onClick={async () => {
                        for (const action of currentDictation.actions) {
                          if (!action.is_approved) {
                            await approveAction(action.id);
                          }
                        }
                      }}
                      className="w-full py-2 bg-green-500 text-white rounded-lg mt-4 hover:bg-green-600"
                    >
                      Approve All
                    </button>
                  )}
                  
                  {currentDictation.actions.every(a => a.is_approved) && 
                   currentDictation.actions.some(a => !a.is_executed) && (
                    <button
                      onClick={async () => {
                        for (const action of currentDictation.actions) {
                          if (action.is_approved && !action.is_executed) {
                            await executeAction(action.id);
                          }
                        }
                      }}
                      className="w-full py-2 bg-virgo-teal-500 text-white rounded-lg mt-4 hover:bg-virgo-teal-600"
                    >
                      Execute All
                    </button>
                  )}
                </>
              ) : (
                <div className="text-center text-virgo-teal-400 py-8">
                  No actions detected
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-virgo-teal-400">
            <div className="text-center">
              <Mic className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>Select a dictation or start a new recording</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dictate;
